import React, { MouseEvent } from "react";
import discountBadge from "../../assets/badge-discount.svg";
import { ActiveProduct } from "../../build/activeEntityType";
import { divide, multiply, subtract } from "../../utils/math";
import { NodeStates, SimpleCartExpandedNode, StateManager } from "../../build/types";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import DynamicIcon from "../DynamicIcon";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const getTotalPrice = (p: ActiveProduct) => {
    const totalPrice = multiply(p.price, p.quantity);
    return totalPrice;
};

/** Line total after product discount (base for product fee). */
const getLineTotalAfterProductDiscount = (p: ActiveProduct): number => {
    const totalPrice = multiply(p.price, p.quantity);
    if (!p.discount || p.discount.value <= 0) return totalPrice;
    const discountAmount = p.discount.isPercent ? multiply(p.discount.value, totalPrice) : p.discount.value;
    return Math.max(0, subtract(totalPrice, discountAmount));
};

/** Product fee amount: fee is applied to discounted line total, not normal price. */
const getProductFeeDisplayAmount = (p: ActiveProduct): number => {
    if (!p.fee || p.fee.amount <= 0) return 0;
    const lineAfterDiscount = getLineTotalAfterProductDiscount(p);
    return p.fee.isPercent ? multiply(p.fee.amount, lineAfterDiscount) : p.fee.amount;
};

export interface CalculatedAmounts {
    discountAmount?: number;
    feeAmount?: number;
    discountLabel?: string;
    feeLabel?: string;
}

const SimpleCartProduct = ({
    product,
    calculatedAmounts,
    onRemove,
    onSelect,
    dividerBg,
    productStyle,
    productSubtextStyle,
    expandedNode,
    formatCurrency,
    nodeState,
    stateManager,
    builder,
    isCFD,
    disabled
}: {
    product: ActiveProduct;
    calculatedAmounts?: CalculatedAmounts;
    onRemove?: () => void;
    onSelect?: (e: MouseEvent) => void;
    dividerBg?: string;
    productStyle?: React.CSSProperties;
    productSubtextStyle?: React.CSSProperties;
    formatCurrency: (amount: number) => string;
    stateManager?: StateManager;
    disabled?: boolean;
    builder?: boolean;
    nodeState?: NodeStates;
    isCFD?: boolean;
    expandedNode?: SimpleCartExpandedNode;
}) => {
    let DeleteIcon = (
        <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" stroke="#ABA7AF" />
            <path d="M5 5L13 13M5 13L13 5" stroke="#ABA7AF" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const { states: buttonStates, setElement: setButtonElement } = useInteractionStates<HTMLSpanElement>();

    const { name, quantity, price, discount, note, fee, sku, attributes } = product;
    const {
        showNote,
        showSku,
        showSkuLabel,
        showItemUnit,
        showItemUnitLabel,
        deleteItemIcon,
        showDeleteIcon,
        showVariationDetails,
        showDiscountAndFees
    } = expandedNode as SimpleCartExpandedNode;

    if (deleteItemIcon?.src) {
        const style = getCompoundStyle({
            otherStyle: deleteItemIcon?.style,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_deleteItemIcon_style")
        });
        const colors =
            getStateColorsForBuilder(stateManager, nodeState, "expandedNode_deleteItemIcon_colors") || deleteItemIcon?.colors;
        DeleteIcon = <DynamicIcon name={deleteItemIcon.src} style={style} colors={colors} />;
    }

    if (builder) {
        return (
            <article style={dividerBg ? { borderColor: dividerBg } : {}}>
                <div className="product-row">
                    <div style={{ marginBottom: "8px" }}>
                        <div className="product-main-row">
                            <div className="product-main-row">
                                <span className="product-qty" style={productStyle}>
                                    {quantity} x{" "}
                                    <span className="prod-name" style={productStyle}>
                                        {name}
                                    </span>
                                </span>
                                <span className="product-price" style={productStyle}>
                                    {formatCurrency(getTotalPrice(product))}
                                </span>
                            </div>

                            {!isCFD && showDeleteIcon && (
                                <div className="delete-action" data-testid={"remove-product-from-cart-button"}>
                                    <span
                                        className="btn"
                                        onClick={() => !disabled && onRemove && onRemove()}
                                        ref={setButtonElement}
                                    >
                                        {DeleteIcon}
                                    </span>
                                </div>
                            )}
                        </div>

                        {quantity > 1 && (
                            <div className="product-extra-row">
                                <span className="grey unit-price" style={productSubtextStyle}>
                                    {formatCurrency(divide(getTotalPrice(product), quantity))} {showItemUnit && showItemUnitLabel}
                                </span>
                            </div>
                        )}

                        <div className="product-extra-row">
                            <span className="grey sku" style={productSubtextStyle}>
                                {showVariationDetails && attributes}
                            </span>
                        </div>

                        <div className="product-extra-row">
                            <span className="grey sku" style={productSubtextStyle}>
                                {showSku && sku && `${showSkuLabel} ${sku}`}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {showDiscountAndFees && (
                            <>
                                {(calculatedAmounts?.discountAmount != null && calculatedAmounts.discountAmount > 0
                                    ? true
                                    : discount && discount.value > 0) && (
                                    <div className="product-extra-row">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {calculatedAmounts?.discountAmount != null && calculatedAmounts.discountAmount > 0
                                                ? (calculatedAmounts.discountLabel ?? discount?.label)
                                                : discount?.label}
                                            {calculatedAmounts?.discountAmount == null &&
                                                discount?.isPercent &&
                                                ` (${multiply(discount.value, 100)}%)`}
                                        </span>
                                        <div className="discount-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                -
                                                {formatCurrency(
                                                    calculatedAmounts?.discountAmount != null &&
                                                        calculatedAmounts.discountAmount > 0
                                                        ? calculatedAmounts.discountAmount
                                                        : discount!.isPercent
                                                          ? multiply(discount!.value, price * quantity)
                                                          : discount!.value
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                    ? true
                                    : fee && fee.amount > 0) && (
                                    <div className="product-extra-row indent">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                                ? (calculatedAmounts.feeLabel ?? fee?.label)
                                                : fee?.label}
                                            {calculatedAmounts?.feeAmount == null &&
                                                fee?.isPercent &&
                                                ` (${multiply(fee.amount, 100)}%)`}
                                        </span>
                                        <div className="fee-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                +
                                                {formatCurrency(
                                                    calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                                        ? calculatedAmounts.feeAmount
                                                        : getProductFeeDisplayAmount(product)
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {showNote && note && (
                            <span className="product-extra" style={productSubtextStyle}>
                                Note: {note}
                            </span>
                        )}
                    </div>
                </div>
            </article>
        );
    } else {
        if (deleteItemIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states: buttonStates,
                defaultStyle: deleteItemIcon.style,
                stateManager,
                paths: ["expandedNode_deleteItemIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states: buttonStates,
                stateManager,
                paths: ["expandedNode_deleteItemIcon_colors"],
                defaultColors: deleteItemIcon.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            DeleteIcon = <DynamicIcon name={deleteItemIcon.src} style={{ width: "18px", ...style }} colors={colors} />;
        }

        return (
            <article style={dividerBg ? { borderColor: dividerBg } : {}}>
                <div
                    className="product-row"
                    onClick={e => {
                        e.stopPropagation();
                        onSelect && onSelect(e);
                    }}
                >
                    <div style={{ marginBottom: "8px" }}>
                        <div className="product-main-row">
                            <div className="product-main-row">
                                <span className="product-qty" style={productStyle}>
                                    {quantity} x{" "}
                                    <span className="prod-name" style={productStyle}>
                                        {name}
                                    </span>
                                </span>
                                <span className="product-price" style={productStyle}>
                                    {formatCurrency(getTotalPrice(product))}
                                </span>
                            </div>

                            {!isCFD && showDeleteIcon && (
                                <div className="delete-action" data-testid={"remove-product-from-cart-button"}>
                                    <span
                                        className="btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            !disabled && onRemove && onRemove();
                                        }}
                                        ref={setButtonElement}
                                    >
                                        {DeleteIcon}
                                    </span>
                                </div>
                            )}
                        </div>

                        {quantity > 1 && (
                            <div className="product-extra-row">
                                <span className="grey unit-price" style={productSubtextStyle}>
                                    {formatCurrency(divide(getTotalPrice(product), quantity))} {showItemUnit && showItemUnitLabel}
                                </span>
                            </div>
                        )}

                        <div className="product-extra-row">
                            <span className="grey sku" style={productSubtextStyle}>
                                {showVariationDetails && attributes}
                            </span>
                        </div>

                        <div className="product-extra-row">
                            <span className="grey sku" style={productSubtextStyle}>
                                {showSku && sku && `${showSkuLabel} ${sku}`}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {showDiscountAndFees && (
                            <>
                                {(calculatedAmounts?.discountAmount != null && calculatedAmounts.discountAmount > 0
                                    ? true
                                    : discount && discount.value > 0) && (
                                    <div className="product-extra-row">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {calculatedAmounts?.discountAmount != null && calculatedAmounts.discountAmount > 0
                                                ? (calculatedAmounts.discountLabel ?? discount?.label)
                                                : discount?.label}
                                            {calculatedAmounts?.discountAmount == null &&
                                                discount?.isPercent &&
                                                ` (${multiply(discount.value, 100)}%)`}
                                        </span>
                                        <div className="discount-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                -
                                                {formatCurrency(
                                                    calculatedAmounts?.discountAmount != null &&
                                                        calculatedAmounts.discountAmount > 0
                                                        ? calculatedAmounts.discountAmount
                                                        : discount!.isPercent
                                                          ? multiply(discount!.value, price * quantity)
                                                          : discount!.value
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                    ? true
                                    : fee && fee.amount > 0) && (
                                    <div className="product-extra-row indent">
                                        <span className="product-extra" style={productSubtextStyle}>
                                            {!productSubtextStyle && <img src={discountBadge} />}
                                            {calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                                ? (calculatedAmounts.feeLabel ?? fee?.label)
                                                : fee?.label}
                                            {calculatedAmounts?.feeAmount == null &&
                                                fee?.isPercent &&
                                                ` (${multiply(fee.amount, 100)}%)`}
                                        </span>
                                        <div className="fee-value-container">
                                            <div className="grey" style={productSubtextStyle}>
                                                +
                                                {formatCurrency(
                                                    calculatedAmounts?.feeAmount != null && calculatedAmounts.feeAmount > 0
                                                        ? calculatedAmounts.feeAmount
                                                        : getProductFeeDisplayAmount(product)
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {showNote && note && (
                            <span className="product-extra" style={productSubtextStyle}>
                                Note: {note}
                            </span>
                        )}
                    </div>
                </div>
            </article>
        );
    }
};

export default SimpleCartProduct;
